import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import { Tabs, TabsContent, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

interface SaengMyongYangProps {
  selectedBelt: string;
  getBeltColor: (belt: string) => string;
  presentation?: "legacy" | "curriculum";
  beltTabSurfaceClass?: string;
}

const START_EAGLE_LINES = [
  "Right leg steps back into Eagle Stance (Short back stance) Fingertips forward",
  "Raise the arms like wings, keep them under the shoulders",
  "Raise the breastbone and raise the arms slightly higher, shaking the wings from the shoulders so the fingertips move; the motion comes from the arms, not the fingers. The arms are completely relaxed.",
];

function SaengMyongYangPatternTab() {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key</h4>
        <ul className="list-inside list-disc space-y-1 text-gray-700">
          <li>OH = Open Hand</li>
          <li>R = Right</li>
          <li>L = Left</li>
          <li>S = Stay in Stance</li>
          <li>UB = Upper Block</li>
          <li>LB = Low Block</li>
          <li>M = Middle</li>
          <li>Sudo = Knife-hand strike</li>
        </ul>
      </div>

      <div className="space-y-2 text-gray-700">
        <p className="font-medium">START:</p>
        <p className="font-semibold text-taekwondo-red">Joonbi</p>
        <p>Bow, Junbee (5 count)</p>
        <p>Both say &quot;Saeng Myeong&quot; = A says &quot;Um.&quot; B says &quot;Yang&quot; Both say &quot;Life!&quot;</p>
        <ol className="mt-3 list-none space-y-2">
          {START_EAGLE_LINES.map((line, index) => (
            <li key={index} className="flex gap-2">
              <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">{index + 1}.</span>
              <span>{line}</span>
            </li>
          ))}
        </ol>
      </div>

      <SaengMyongYangMovesTables />
      <SaengMyongYangNotes />
    </div>
  );
}

function SaengMyongYangMovesTables() {
  return (
    <>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Moves</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["1", "Raise left leg like Cobra, then attack with R Hand Tiger-mouth throat strike", "L"],
                ["2", "Parry with right hand from out to in", "S"],
                ["3", "Block L OH out to in, Face level", "S"],
                ["4", "Right Punch to face", "S"],
                ["5", "Slide back keeping L leg forward, Left OH block in to out, ROH low", "L"],
                ["6", "Set in back stance; OH RUB, LLB (Moo Gong)", "L"],
                ["7", "Shuffle back with Low X block", ""],
                ["8", "Jump back with low X Block", ""],
                ["9", "and Step back into R Upper Block Back stance", "R Back"],
                ["10", "Right drop block against the punch", "S"],
                ["11", "Right Revers Hammer punch to Temple", "S Front"],
                ["12", "Left Reverse Punch to face", "S"],
                ["13", "Shuffle forward with left ridge hand strike", "R Front"],
                ["14", "Step left (two complete steps quickly ending with a punch)", ""],
                ["15", "Step right", ""],
                ["16", "Left reverse punch", "R Front"],
                ["17", "Block with right open hand from out to in", ""],
                ["18", "Set in short back stance; OH RUB, LLB (Moo Gong)", "R"],
                ["19", "Slide back right, left and block the kick with right OH block out to in", "R"],
                ["20", "Slide/skip back + block the neck grab with right OH block out to in", "R"],
                ["21", "Step back diagonally to R with R into left eagle stance arms crossed", "L Eagle"],
                ["22", "Lean away opening wings", "L"],
              ].map(([num, move, stance]) => (
                <tr key={num} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Ending 1</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["23", "Right Roundhouse kick and set down forwards (short)", "R"],
                ["24", "Block the neck strike with right from in to out and shuffle back a little keeping right leg forward", "R"],
                ["25", "(Partner grabs arm and sweeps front R leg)", ""],
                ["26", "Lift right front leg and", ""],
                ["27", "Sidekick and set down with right leg forwards", "R"],
                ["28", "Left reverse open hand block to the ground with right open hand up in the air, elbow bent 90° Low front stance", "S"],
                ["29", "Lean back like Balsak So for the next 4 moves", ""],
                ["30", "R Open hand low block (wipe left with right) left fist chamber", "S"],
                ["31", "Bring left hand up like wing opening", "S"],
                ["32", "Bring left hand up like wing opening, open hand slightly", "S"],
                ["33", "Bring left hand up like wing opening, open completely", "S"],
                ["34", "Twist to right like X stance with left Sudo block counterclockwise", "S"],
              ].map(([num, move, stance]) => (
                <tr key={`e1-${num}`} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div>
        <h4 className="mb-3 text-base font-medium text-taekwondo-red">Ending 2</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-gray-700">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left font-medium">#</th>
                <th className="p-2 text-left font-medium">Movement</th>
                <th className="p-2 text-left font-medium">Stance</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["23", "Right Roundhouse kick to head", "R"],
                ["24", "Fall onto left side", "L"],
                ["25", "Front right leg sidekick from the ground", ""],
                ["26", "Retract leg and Plant right foot in the ground, push off and get up quickly into right forward back stance", "R"],
                ["27", "Lean back like Balsak So for the next 4 moves", ""],
                ["28", "Right Open hand low block (wipe left with right) left fist chamber", "S"],
                ["29", "Bring left hand up like wing opening", "S"],
                ["30", "Bring left hand up like wing opening", "S"],
                ["31", "Bring left hand up like wing opening", "S"],
                ["32", "Twist to right like X stance with left Sudo block counterclockwise", "S"],
              ].map(([num, move, stance]) => (
                <tr key={`e2-${num}`} className="border-b">
                  <td className="p-2 font-medium text-taekwondo-red">{num}</td>
                  <td className="p-2">{move}</td>
                  <td className="p-2">{stance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function SaengMyongYangNotes() {
  return (
    <div>
      <h4 className="mb-3 text-base font-medium text-taekwondo-red">Notes</h4>
      <div className="space-y-4 text-gray-700">
        <div>
          <p className="font-medium">Middle transition:</p>
          <p>
            If switching roles, UM (A) steps up into Eagle stance and YANG (B) steps back into Left forward
            Eagle stance, then the roles switch.
          </p>
        </div>
        <div>
          <p className="font-medium">END:</p>
          <ol className="mt-2 list-none space-y-2">
            {[
              "Junbee Right leg back into Eagle Stance (Short back stance) Left foot Forward Fingertips forward",
              "Raise the arms like wings, keep them under the shoulders",
              "Raise the shoulders like hunching and raise the arms slightly higher, shaking the wings so the fingertips move, but the motion comes from the arms, not the fingers. The arms are completely relaxed.",
            ].map((line, index) => (
              <li key={index} className="flex gap-2">
                <span className="shrink-0 font-semibold text-taekwondo-red tabular-nums">{index + 1}.</span>
                <span>{line}</span>
              </li>
            ))}
          </ol>
          <p className="mt-2">UM (A) steps up, YANG (B) steps back to Junbee</p>
        </div>
      </div>
    </div>
  );
}

export default function SaengMyongYang({
  selectedBelt,
  getBeltColor: _getBeltColor,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: SaengMyongYangProps) {
  // Only show for D5
  if (!["BLACK/D5"].includes(selectedBelt)) {
    return null;
  }

  const surface = beltTabSurfaceClass;

  if (presentation === "curriculum") {
    return (
      <div className="rounded-lg bg-gray-50 p-4 md:p-5">
        <Tabs defaultValue="pattern-information" className="w-full space-y-4">
          <ScrollableCurriculumTabStrip ariaLabelPrefix="SaengMyong Yang">
            <TabsTrigger
              value="pattern-information"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern Information
            </TabsTrigger>
            <TabsTrigger
              value="meaning"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Meaning
            </TabsTrigger>
            <TabsTrigger
              value="movements"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Movements
            </TabsTrigger>
            <TabsTrigger
              value="key-techniques"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Key Techniques
            </TabsTrigger>
            <TabsTrigger
              value="pattern"
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-black/10 px-3 py-2 text-xs font-medium md:text-sm",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              Pattern
            </TabsTrigger>
          </ScrollableCurriculumTabStrip>

          <TabsContent value="pattern-information" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Pattern Information</h4>
              <p className="text-gray-700">
                SaengMyongYang is a grandmaster-level pattern that represents the cultivation and expression of
                life force energy. This pattern is exclusively for 5th Dan Black Belt practitioners, embodying
                the highest level of spiritual and energetic development in martial arts practice.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="meaning" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Meaning</h4>
              <p className="text-gray-700">
                SaengMyongYang translates to &quot;Life Force Light&quot; or &quot;Illuminated Life Energy,&quot;
                representing the highest expression of martial arts mastery where the practitioner becomes a
                beacon of enlightened movement and spiritual understanding.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="movements" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Movements</h4>
              <p className="text-gray-700">
                The pattern consists of 81 movements that demonstrate the complete mastery of life force energy.
                Each movement represents a different aspect of energy cultivation and expression, forming a
                complete system of martial and spiritual development.
              </p>
            </div>
          </TabsContent>
          <TabsContent value="key-techniques" className="mt-0 focus-visible:outline-none">
            <div>
              <h4 className="mb-3 text-base font-medium text-taekwondo-red">Key Techniques</h4>
              <ul className="list-inside list-disc space-y-1 text-gray-700">
                <li>Advanced energy cultivation</li>
                <li>Spiritual projection methods</li>
                <li>Enlightened movement principles</li>
                <li>Complete energy mastery</li>
                <li>Internal-external harmony</li>
                <li>Light energy manifestation</li>
              </ul>
            </div>
          </TabsContent>
          <TabsContent value="pattern" className="mt-0 focus-visible:outline-none">
            <SaengMyongYangPatternTab />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-4 rounded-lg mt-2">
      <div className="space-y-4">
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Pattern Information</h4>
          <p className="text-gray-700 mb-4">
            SaengMyongYang is a grandmaster-level pattern that represents the cultivation and expression of life force energy. This pattern is
            exclusively for 5th Dan Black Belt practitioners, embodying the highest level of spiritual and energetic development in martial arts
            practice.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Meaning</h4>
          <p className="text-gray-700">
            SaengMyongYang translates to &quot;Life Force Light&quot; or &quot;Illuminated Life Energy,&quot; representing the highest expression of
            martial arts mastery where the practitioner becomes a beacon of enlightened movement and spiritual understanding.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Movements</h4>
          <p className="text-gray-700">
            The pattern consists of 81 movements that demonstrate the complete mastery of life force energy. Each movement represents a different aspect
            of energy cultivation and expression, forming a complete system of martial and spiritual development.
          </p>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Key Techniques</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>Advanced energy cultivation</li>
            <li>Spiritual projection methods</li>
            <li>Enlightened movement principles</li>
            <li>Complete energy mastery</li>
            <li>Internal-external harmony</li>
            <li>Light energy manifestation</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Key</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            <li>OH = Open Hand</li>
            <li>R = Right</li>
            <li>L = Left</li>
            <li>S = Stay in Stance</li>
            <li>UB = Upper Block</li>
            <li>LB = Low Block</li>
            <li>M = Middle</li>
            <li>Sudo = Knife-hand strike</li>
          </ul>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Joonbi (Starting Position)</h4>
          <div className="text-gray-700 space-y-2">
            <p className="font-medium">START:</p>
            <p>Bow, Junbee (5 count)</p>
            <p>Both say "Saeng Myeong" = A says "Um." B says "Yang" Both say "Life!"</p>
            <ol className="list-decimal list-inside space-y-2 pl-4">
              <li>Right leg steps back into Eagle Stance (Short back stance) Fingertips forward</li>
              <li>Raise the arms like wings, keep them under the shoulders</li>
              <li>Raise the breastbone and raise the arms slightly higher, shaking the wings from the shoulders so the fingertips move; the motion comes from the arms, not the fingers. The arms are completely relaxed.</li>
            </ol>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Moves</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["1", "Raise left leg like Cobra, then attack with R Hand Tiger-mouth throat strike", "L"],
                  ["2", "Parry with right hand from out to in", "S"],
                  ["3", "Block L OH out to in, Face level", "S"],
                  ["4", "Right Punch to face", "S"],
                  ["5", "Slide back keeping L leg forward, Left OH block in to out, ROH low", "L"],
                  ["6", "Set in back stance; OH RUB, LLB (Moo Gong)", "L"],
                  ["7", "Shuffle back with Low X block", ""],
                  ["8", "Jump back with low X Block", ""],
                  ["9", "and Step back into R Upper Block Back stance", "R Back"],
                  ["10", "Right drop block against the punch", "S"],
                  ["11", "Right Revers Hammer punch to Temple", "S Front"],
                  ["12", "Left Reverse Punch to face", "S"],
                  ["13", "Shuffle forward with left ridge hand strike", "R Front"],
                  ["14", "Step left (two complete steps quickly ending with a punch)", ""],
                  ["15", "Step right", ""],
                  ["16", "Left reverse punch", "R Front"],
                  ["17", "Block with right open hand from out to in", ""],
                  ["18", "Set in short back stance; OH RUB, LLB (Moo Gong)", "R"],
                  ["19", "Slide back right, left and block the kick with right OH block out to in", "R"],
                  ["20", "Slide/skip back + block the neck grab with right OH block out to in", "R"],
                  ["21", "Step back diagonally to R with R into left eagle stance arms crossed", "L Eagle"],
                  ["22", "Lean away opening wings", "L"],
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Ending 1</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["23", "Right Roundhouse kick and set down forwards (short)", "R"],
                  ["24", "Block the neck strike with right from in to out and shuffle back a little keeping right leg forward", "R"],
                  ["25", "(Partner grabs arm and sweeps front R leg)", ""],
                  ["26", "Lift right front leg and", ""],
                  ["27", "Sidekick and set down with right leg forwards", "R"],
                  ["28", "Left reverse open hand block to the ground with right open hand up in the air, elbow bent 90° Low front stance", "S"],
                  ["29", "Lean back like Balsak So for the next 4 moves", ""],
                  ["30", "R Open hand low block (wipe left with right) left fist chamber", "S"],
                  ["31", "Bring left hand up like wing opening", "S"],
                  ["32", "Bring left hand up like wing opening, open hand slightly", "S"],
                  ["33", "Bring left hand up like wing opening, open completely", "S"],
                  ["34", "Twist to right like X stance with left Sudo block counterclockwise", "S"],
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Ending 2</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-gray-700">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left p-2 font-medium">#</th>
                  <th className="text-left p-2 font-medium">Movement</th>
                  <th className="text-left p-2 font-medium">Stance</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["23", "Right Roundhouse kick to head", "R"],
                  ["24", "Fall onto left side", "L"],
                  ["25", "Front right leg sidekick from the ground", ""],
                  ["26", "Retract leg and Plant right foot in the ground, push off and get up quickly into right forward back stance", "R"],
                  ["27", "Lean back like Balsak So for the next 4 moves", ""],
                  ["28", "Right Open hand low block (wipe left with right) left fist chamber", "S"],
                  ["29", "Bring left hand up like wing opening", "S"],
                  ["30", "Bring left hand up like wing opening", "S"],
                  ["31", "Bring left hand up like wing opening", "S"],
                  ["32", "Twist to right like X stance with left Sudo block counterclockwise", "S"],
                ].map(([num, move, stance]) => (
                  <tr key={num} className="border-b">
                    <td className="p-2 text-taekwondo-red font-medium">{num}</td>
                    <td className="p-2">{move}</td>
                    <td className="p-2">{stance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h4 className="font-medium text-base mb-3 text-taekwondo-red">Notes</h4>
          <div className="text-gray-700 space-y-4">
            <div>
              <p className="font-medium">Middle transition:</p>
              <p>If switching roles, UM (A) steps up into Eagle stance and YANG (B) steps back into Left forward Eagle stance, then the roles switch.</p>
            </div>
            <div>
              <p className="font-medium">END:</p>
              <ol className="list-decimal list-inside space-y-2 pl-4">
                <li>Junbee Right leg back into Eagle Stance (Short back stance) Left foot Forward Fingertips forward</li>
                <li>Raise the arms like wings, keep them under the shoulders</li>
                <li>Raise the shoulders like hunching and raise the arms slightly higher, shaking the wings so the fingertips move, but the motion comes from the arms, not the fingers. The arms are completely relaxed.</li>
              </ol>
              <p className="mt-2">UM (A) steps up, YANG (B) steps back to Junbee</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 